import {
  DocumentNode,
  gql,
  NetworkStatus,
  QueryHookOptions,
  useApolloClient,
  useMutation,
  useQuery,
} from '@provision/graphql-client';
import { useCallback, useEffect, useMemo } from 'react';
import { isNullish } from 'remeda';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import {
  Conversation,
  getCurrentUserQueryVariables,
  RuleValues,
  UserGroupRules,
} from 'src/graphql';
import { MAX_QUERY_LIMIT } from 'src/graphql/constants';
import { useConversationAssigneeGroupsOptions } from 'src/graphql/entities/conversation-assignee-group';
import { META_DATA_SEARCH_QUERY_FIELDS } from 'src/graphql/entities/metadata.fragments';
import { formatUserSettings } from 'src/graphql/entities/user-setting-value/utils';
import {
  ConditionInput,
  QueryVariables,
  Records,
  SearchInput,
  SortingInput,
} from 'src/graphql/types';
import { useLoadMore } from 'src/graphql/utils';
import { useGlobalStore } from 'src/store';
import { selectUser, useUserStore } from 'src/store/current-user';
import type { PlatformUserRaw } from 'src/types';
import { useNotifications } from 'src/utils/hooks';
import { useCacheManager } from 'src/utils/hooks/use-cache-manager';
import { removeNotDefinedProperties } from 'src/utils/objects';
import { getPlatformUserAvatarStorageKey } from './platform-user-avatar-url';
import { PLATFORM_FIELDS, PLATFORM_RELATIONS } from './platform-user.fragments';
import { mapPlatformUsersToSortedOptions } from './platform-user.mapping';
import { useDeletePlatformUserAvatar } from './use-delete-platform-user-avatar';
import {
  PLATFORM_USER_AVATAR_FRAGMENT,
  PlatformUserAvatarFields,
} from './use-platform-user-avatar-lookup';

export const GET_PLATFORM_USERS_WITHOUT_RELATIONS_QUERY = gql`
  query GetNoRelationsPlatformUsers(
    $params: QueryParamsWithSearch
    $skipTotalRecords: Boolean = false
  ) @entity(name: "PlatformUser") {
    getPlatformUser(queryParams: $params) {
      records {
        ...PlatformUserFields
      }
      metadata {
        ...MetaDataSearchQueryFields
      }
    }
  }
  ${PLATFORM_FIELDS}
  ${META_DATA_SEARCH_QUERY_FIELDS}
`;

export const GET_PLATFORM_USERS_QUERY = gql`
  query GetPlatformUser($params: QueryParamsWithSearch, $skipTotalRecords: Boolean = false)
  @entity(name: "PlatformUser") {
    getPlatformUser(queryParams: $params) {
      records {
        ...PlatformUserFields
        ...PlatformUserRelations
      }
      metadata {
        ...MetaDataSearchQueryFields
      }
    }
  }
  ${PLATFORM_FIELDS}
  ${PLATFORM_RELATIONS}
  ${META_DATA_SEARCH_QUERY_FIELDS}
`;

const UPDATE_PLATFORM_USER_MUTATION = gql`
  mutation UpdatePlatformUser($conditions: [ConditionInput], $data: PlatformUserDataInput) {
    updatePlatformUser(conditions: $conditions, data: $data) {
      ...PlatformUserFields
    }
  }
  ${PLATFORM_FIELDS}
`;

const CREATE_PLATFORM_USER_MUTATION = gql`
  mutation CreateUser($data: PlatformUserDataInput!) {
    createUser(params: $data) {
      userId
      cognitoUsername
    }
  }
`;

const UPDATE_PLATFORM_USER_ACTIVE_STATE_MUTATION = gql`
  mutation UpdateUserActiveState($data: UpdateUserActiveStateDataInput!) {
    updateUserActiveState(params: $data) {
      userId
    }
  }
`;

const SET_USER_PASSWORD_MUTATION = gql`
  mutation SetUserPassword($data: SetUserPasswordDataInput!) {
    setUserPassword(params: $data) {
      userId
    }
  }
`;

export type ServerPlatformUserRaw = PlatformUserRaw & { id: string };
export type CreateUser = {
  userId: string;
  cognitoUsername: string;
};

export type GetPlatformUserQuery = {
  getPlatformUser: Records<ServerPlatformUserRaw>;
};

export type PlatformUser = Pick<
  PlatformUserRaw,
  | 'userId'
  | 'partnerId'
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'phoneNumber'
  | 'userGroups'
  | 'isActive'
  | 'userCommunicationPreferences'
  | 'avatarFileKey'
> & { id: string; fullName: string; userSettingValues?: PlatformUserRaw['userSettingValues'] };

export interface ConversationAssigneeOption {
  groupId: string;
  group: string;
  id: string;
  userName: string;
  label: string;
  userId: string | null;
  value: string;
  isActive: boolean;
  partnerId?: number | null;
  avatarFileKey?: string | null;
}

export interface SetUserPasswordParams {
  userId: string;
  temporaryPassword?: string;
}

function filterPlatformUsersByRule(
  users: ServerPlatformUserRaw[],
  rule: UserGroupRules,
  expectedValue: string,
): ServerPlatformUserRaw[] {
  return users.filter((user) => {
    const isAllowed = user.userGroups?.find((userGroup) =>
      userGroup.ruleValues.find(
        (userRule) =>
          userRule.userGroupRuleId === (rule as string) &&
          userRule.value?.toLowerCase() === expectedValue,
      ),
    );
    return !!isAllowed;
  });
}

export function usePlatformUsers(
  params: {
    partnerId?: number | null;
    shouldIncludePlatformPartnerId?: boolean;
    filters?: ConditionInput[];
    limit?: number;
    query?: DocumentNode;
    searchParams?: SearchInput;
    removeNestedFilters?: boolean;
    sort?: SortingInput | SortingInput[];
  },
  options?: QueryHookOptions<GetPlatformUserQuery>,
) {
  const {
    partnerId,
    shouldIncludePlatformPartnerId = true,
    limit,
    query,
    searchParams,
    removeNestedFilters = true,
    sort,
  } = params;

  const isMultiTenancyMode = useGlobalStore.use.isMultiTenancyMode();

  const hasFilterByUserGroup = (params.filters ?? []).some(
    (item) => item.attribute === 'userGroups/ruleValues/userGroupRuleId',
  );

  const queryParams = useMemo(() => {
    const values = [];

    if (!isNullish(partnerId)) {
      values.push(partnerId);
    }

    if (shouldIncludePlatformPartnerId) {
      values.unshift(PLATFORM_PARTNER_ID);
    }

    // TODO: this can be removed once 1V-3011 gets fixed
    const nonNestedFilters =
      params.filters?.filter(
        // Filtering by userGroups/ruleValues will not only filter the PlatformUsers
        // but will also filter the attribute userGroups in the response, so we need to remove it
        (item) => item.attribute !== 'userGroups/ruleValues/userGroupRuleId',
      ) ?? [];

    let filters = [...(removeNestedFilters ? nonNestedFilters : (params.filters ?? []))];
    // added this condition to avoid pushing partner filter as part of a hotfix to not produce too many changes
    // this hook must be refactored to hold all the business specific logic outside, the hook itself should NOT
    // decide to remove or add any filters by some flags, only general/common filters could be added here
    if (!options?.context?.omitIsolationModeHeader) {
      filters.push({
        // this filter has nothing to do with multi-tenancy data access.
        // Depending on the context, it can be for example conversation's partner
        // or the selected partner + platform partner.
        attribute: 'partnerId',
        operator: 'in',
        value: values.join(','),
      });
    }
    // remove partner id filter if user in multitenancy mode and partner id was not provided
    // WARNING: This could remove all filters which will fetch all users for all partners so
    // it will be important to handle pagination properly
    if (isMultiTenancyMode && isNullish(partnerId)) {
      filters = filters.filter((item) => item.attribute !== 'partnerId');
    }

    return {
      filters,
      limit: limit ?? MAX_QUERY_LIMIT,
      searchParams,
      sorting: sort ?? { attribute: 'createdAt', desc: true },
    };
  }, [
    options?.context?.omitIsolationModeHeader,
    partnerId,
    shouldIncludePlatformPartnerId,
    params.filters,
    removeNestedFilters,
    isMultiTenancyMode,
    limit,
    searchParams,
    sort,
  ]);

  const useQueryResult = useQuery<GetPlatformUserQuery, QueryVariables>(
    query ?? GET_PLATFORM_USERS_QUERY,
    {
      ...options,
      // when user is in multiTenancyMode we should not skip request when no partnerId provided
      // users can have an access to all partner ids ( which is stored as "*" of available_partner_ids rule)
      skip:
        (!isMultiTenancyMode &&
          isNullish(partnerId) &&
          !options?.context?.omitIsolationModeHeader) ||
        options?.skip,
      notifyOnNetworkStatusChange: true,
      variables: {
        params: queryParams,
      },
      context: {
        omitIsolationModeHeader: options?.context?.omitIsolationModeHeader,
        includePlatformLevelData: true, // This needs to be true to retrieve conversationAssigneeGroups that contains partnerId 0
      },
    },
  );

  const { data, fetchMore, variables, networkStatus, ...rest } = useQueryResult;

  const cacheManager = useCacheManager(query ?? GET_PLATFORM_USERS_QUERY, useQueryResult);

  const records = useMemo(
    () => data?.getPlatformUser.records || [],
    [data?.getPlatformUser.records],
  );

  const platformUsers = useMemo(() => {
    const users =
      hasFilterByUserGroup && removeNestedFilters
        ? filterPlatformUsersByRule(
            records,
            UserGroupRules.AccessToUpdateConversation,
            RuleValues.True,
          )
        : records;

    return users.map((user) => ({
      ...user,
      userCommunicationPreferences: user.userCommunicationPreferences?.toSorted(
        (a, b) => b.priority - a.priority,
      ),
    }));
  }, [hasFilterByUserGroup, records, removeNestedFilters]);

  const rawPlatformUsersMap = useMemo(
    () => new Map(platformUsers.map((i) => [i.id, i])),
    [platformUsers],
  );

  const totalRecords = data?.getPlatformUser?.metadata?.totalRecords ?? 0;

  const { loadMore, hasReachedEnd } = useLoadMore<GetPlatformUserQuery>({
    offset: records?.length ?? 0,
    fetchMore,
    params: variables?.params!,
  });

  return {
    rawPlatformUsersMap,
    platformUsers,
    totalRecords,
    loadMore,
    isLoadingMore: networkStatus === NetworkStatus.fetchMore,
    hasMore: data?.getPlatformUser?.metadata?.totalRecords
      ? records?.length < totalRecords
      : !hasReachedEnd,
    isLoaded: !!data?.getPlatformUser?.records,
    variables,
    cacheManager,
    ...rest,
  };
}

export function usePlatformUserIds(
  params: {
    partnerId?: number | null;
    shouldIncludePlatformPartnerId?: boolean;
    filters?: ConditionInput[];
    limit?: number;
    removeNestedFilters?: boolean;
  },
  options?: QueryHookOptions<GetPlatformUserQuery>,
) {
  const { platformUsers, ...rest } = usePlatformUsers(
    {
      ...params,
      query: GET_PLATFORM_USERS_WITHOUT_RELATIONS_QUERY,
    },
    options,
  );

  const data = useMemo(() => platformUsers.map((item) => item.id), [platformUsers]);

  return { data, ...rest };
}

export const useCurrentUserProfile = () => {
  return { user: useUserStore(selectUser) };
};

export function useFetchCurrentUserProfile() {
  const setUser = useUserStore.getState().setUser;
  const removeUser = useUserStore.getState().removeUser;

  const { data, loading, error, refetch } = useQuery<GetPlatformUserQuery>(
    GET_PLATFORM_USERS_QUERY,
    {
      variables: getCurrentUserQueryVariables(),
      fetchPolicy: 'cache-first',
      context: { omitIsolationModeHeader: true },
    },
  );

  const mapped = useMemo(() => {
    const rec = data?.getPlatformUser?.records?.[0];
    if (!rec) return null;
    return {
      id: rec.id,
      userId: rec.userId,
      partnerId: rec.partnerId,
      firstName: rec.firstName,
      lastName: rec.lastName,
      email: rec.email,
      createdAt: rec.createdAt,
      avatarFileKey: rec.avatarFileKey ?? undefined,
      conversationAssigneeGroups: rec.conversationAssigneeGroups ?? [],
      userGroups: rec.userGroups,
      userCommunicationPreferences: rec.userCommunicationPreferences,
      userSettingValueMap: formatUserSettings(rec.userSettingValues),
    };
  }, [data]);

  useEffect(() => {
    // write to store only when it actually changes
    const prev = useUserStore.getState().user;
    const same = (!!prev && !!mapped && prev === mapped) || (!prev && !mapped);
    // currentUser is badly typed, has to be refactored, not a quick thing as a lot of places rely on it
    if (!same) {
      setUser(mapped);
    }
    if (!mapped && !loading && !error) {
      removeUser();
    }
  }, [mapped, loading, error, setUser, removeUser]);

  return { loading, error, refetch };
}

export function usePlatformUser(params: { partnerId?: number | null }) {
  const { showErrorAlert } = useNotifications();
  const apolloClient = useApolloClient();
  const { deleteAvatar } = useDeletePlatformUserAvatar();
  const [updatePlatformUserMutation] = useMutation<{
    updatePlatformUser: PlatformUserRaw[];
  }>(UPDATE_PLATFORM_USER_MUTATION, {
    onError: (error) => {
      console.error(error);
      const message = 'Something went wrong updating the platform user, please try again';
      showErrorAlert(message);
    },
  });

  const [createPlatformUserMutation] = useMutation<{
    createUser: CreateUser;
  }>(CREATE_PLATFORM_USER_MUTATION, {
    onError: (error) => {
      console.error(error);
      let message = 'Something went wrong creating the platform user, please try again';

      if (error.message.includes('User already exists')) {
        message = 'Unable to create user. Email is already being used by another user';
      }
      showErrorAlert(message);
    },
  });

  const [updateUserIsActiveStateMutation] = useMutation<{
    updateUserActiveState: Pick<PlatformUser, 'userId'>;
  }>(UPDATE_PLATFORM_USER_ACTIVE_STATE_MUTATION, {
    onError: (error) => {
      console.error(error);
      const message =
        'Something went wrong updating the platform user active state, please try again';
      showErrorAlert(message);
    },
  });

  const [setUserPasswordMutation] = useMutation<{
    setUserPassword: Pick<PlatformUser, 'userId'>;
  }>(SET_USER_PASSWORD_MUTATION, {
    onError: (error) => {
      console.error(error);
      const message = 'Something went wrong resetting user password, please try again';
      showErrorAlert(message);
    },
  });

  const updatePlatformUser = useCallback(
    async (payload: Partial<PlatformUserRaw>, variables?: QueryVariables) => {
      if (!payload.id) {
        return;
      }

      let previousAvatar: { fileKey: string; partnerId: number | null | undefined } | null =
        null;
      if (payload.avatarFileKey !== undefined) {
        const cached = apolloClient.readFragment<PlatformUserAvatarFields>({
          id: apolloClient.cache.identify({ __typename: 'PlatformUser', id: payload.id }),
          fragment: PLATFORM_USER_AVATAR_FRAGMENT,
        });
        if (cached?.avatarFileKey && cached.avatarFileKey !== payload.avatarFileKey) {
          previousAvatar = {
            fileKey: cached.avatarFileKey,
            partnerId: cached.partnerId ?? params.partnerId,
          };
        }
      }

      const optimisticResponseData = {
        id: payload.id,
        partnerId: params.partnerId,
        firstName: payload.firstName!,
        lastName: payload.lastName!,
        phoneNumber: payload.phoneNumber!,
      } as PlatformUserRaw;

      const patch = removeNotDefinedProperties({
        firstName: payload.firstName,
        lastName: payload.lastName,
        phoneNumber: payload.phoneNumber,
        avatarFileKey: payload.avatarFileKey,
      });

      const { data } = await updatePlatformUserMutation({
        variables: {
          conditions: [
            {
              attribute: 'id',
              operator: '=',
              value: payload.id,
            },
          ],
          data: patch,
        },
        optimisticResponse: {
          updatePlatformUser: [optimisticResponseData],
        },
        update: (cache, { data: response }) => {
          const updated = response?.updatePlatformUser?.[0];
          if (!updated) return;

          cache.updateQuery<GetPlatformUserQuery, QueryVariables>(
            {
              query: GET_PLATFORM_USERS_QUERY,
              variables,
            },
            (existingData) => {
              if (!existingData?.getPlatformUser) return;
              const records = existingData?.getPlatformUser?.records ?? [];
              const updatedRecords = records.map((item) => {
                if (item.id === payload.id) {
                  return {
                    ...item,
                    firstName: payload.firstName,
                    lastName: payload.lastName,
                    phoneNumber: payload.phoneNumber,
                    userGroups: payload.userGroups,
                    avatarFileKey: payload.avatarFileKey ?? item.avatarFileKey,
                  } as PlatformUserRaw;
                }

                return item;
              });

              return {
                getPlatformUser: {
                  ...existingData.getPlatformUser,
                  records: updatedRecords,
                },
              };
            },
          );
        },
      });

      const updatedUser = data?.updatePlatformUser?.[0];

      if (updatedUser && previousAvatar) {
        const storageKey = getPlatformUserAvatarStorageKey(
          previousAvatar.partnerId,
          previousAvatar.fileKey,
        );
        void deleteAvatar(storageKey);
      }

      return updatedUser;
    },
    [apolloClient, deleteAvatar, updatePlatformUserMutation, params],
  );

  const createPlatformUser = useCallback(
    async (payload: Partial<PlatformUserRaw>, variables?: QueryVariables) => {
      if (!payload) {
        return;
      }

      const createdAt = new Date().toISOString();
      const tempId = `temp-id${createdAt}`;

      const optimisticResponseData = {
        userId: tempId,
        cognitoUsername: tempId,
        ...payload,
      } as CreateUser;

      const { data } = await createPlatformUserMutation({
        variables: {
          conditions: [
            {
              attribute: 'id',
              operator: '=',
              value: payload.id,
            },
          ],
          data: {
            ...payload,
            partnerId: params.partnerId,
          },
        },
        optimisticResponse: {
          createUser: optimisticResponseData,
        },
        update: (cache, { data: response }) => {
          const created = response?.createUser;
          if (!created?.userId) return;

          cache.updateQuery<GetPlatformUserQuery, QueryVariables>(
            {
              query: GET_PLATFORM_USERS_QUERY,
              variables,
            },
            (existingData) => {
              if (!existingData?.getPlatformUser) return;
              const { records } = existingData.getPlatformUser;
              // every field GET_PLATFORM_USERS_QUERY selects needs a value, or the list
              // reads as incomplete and refetches right after the create
              const createdRecord = {
                ...payload,
                id: created.userId,
                userId: created.userId,
                cognitoUsername: created.cognitoUsername,
                firstName: payload.firstName!,
                lastName: payload.lastName!,
                email: payload.email!,
                phoneNumber: payload.phoneNumber ?? null,
                avatarFileKey: payload.avatarFileKey ?? null,
                addresses: [],
                isActive: true,
                partnerId: params.partnerId!,
                createdAt,
                updatedAt: createdAt,
                userGroups: [],
                conversationAssigneeGroups: [],
                userCommunicationPreferences: [],
                userSettingValues: [],
                __typename: 'PlatformUser',
              } as ServerPlatformUserRaw;

              const updatedRecords = [
                ...records,
                {
                  ...createdRecord,
                },
              ];

              return {
                getPlatformUser: {
                  ...existingData.getPlatformUser,
                  records: updatedRecords,
                },
              };
            },
          );
        },
      });

      return data?.createUser?.userId;
    },
    [createPlatformUserMutation, params],
  );

  const updatePlatformUserActiveState = useCallback(
    async (payload: Partial<PlatformUserRaw>, variables?: QueryVariables) => {
      if (!payload.userId) {
        return;
      }

      const optimisticResponseData: Pick<PlatformUser, 'userId'> = {
        userId: payload.userId,
      };

      const { data } = await updateUserIsActiveStateMutation({
        variables: {
          data: {
            userId: payload.userId,
            isActive: payload.isActive,
          },
        },
        optimisticResponse: {
          updateUserActiveState: optimisticResponseData,
        },
        update: (cache, { data: response }) => {
          const updated = response?.updateUserActiveState?.userId;
          if (!updated) return;

          cache.updateQuery<GetPlatformUserQuery, QueryVariables>(
            {
              query: GET_PLATFORM_USERS_QUERY,
              variables,
            },
            (existingData) => {
              if (!existingData?.getPlatformUser) return;
              const records = existingData?.getPlatformUser?.records ?? [];

              return {
                getPlatformUser: {
                  ...existingData.getPlatformUser,
                  records: records.filter((item) => item.id !== payload.userId),
                },
              };
            },
          );
        },
      });

      return data?.updateUserActiveState?.userId;
    },
    [updateUserIsActiveStateMutation],
  );

  const setUserPassword = useCallback(
    async (payload: SetUserPasswordParams) => {
      if (!payload.userId) {
        return;
      }

      const { data } = await setUserPasswordMutation({
        variables: {
          data: {
            userId: payload.userId,
            temporaryPassword: payload.temporaryPassword,
          },
        },
      });

      return data?.setUserPassword?.userId;
    },
    [setUserPasswordMutation],
  );

  return {
    updatePlatformUser,
    createPlatformUser,
    updatePlatformUserActiveState,
    setUserPassword,
  };
}

export function usePlatformUsersOptions(
  params: {
    partnerId?: number | null;
    shouldIncludePlatformPartnerId?: boolean;
    filters?: ConditionInput[];
    limit?: number;
    removeNestedFilters?: boolean;
    query?: DocumentNode;
  },
  options?: QueryHookOptions<GetPlatformUserQuery>,
) {
  const { platformUsers, ...rest } = usePlatformUsers(
    {
      ...params,
      query: params.query ?? GET_PLATFORM_USERS_WITHOUT_RELATIONS_QUERY,
    },
    options,
  );

  const allUserRecords = useMemo(() => {
    return mapPlatformUsersToSortedOptions(platformUsers) ?? [];
  }, [platformUsers]);

  return {
    allUserRecords,
    ...rest,
  };
}

export function useConversationAssigneeOptions(conversation: Conversation): {
  selectedAssignee: ConversationAssigneeOption | null;
  assigneeOptions: ConversationAssigneeOption[];
} {
  const conversationAssigneeGroupOptions = useConversationAssigneeGroupsOptions(
    conversation.partnerId,
  );

  const { allUserRecords: assigneeOptions } = usePlatformUsersOptions(
    {
      partnerId: conversation.partnerId,
      filters: [
        {
          attribute: 'conversationAssigneeGroups/id',
          operator: 'in',
          value: conversationAssigneeGroupOptions.map((item) => item.id).join(','),
        },
        {
          attribute: 'userGroups/ruleValues/userGroupRuleId',
          operator: '=',
          value: UserGroupRules.AccessToUpdateConversation,
        },
      ],
      query: GET_PLATFORM_USERS_QUERY,
    },
    {
      skip: !conversationAssigneeGroupOptions.length,
    },
  );

  const conversationAssigneeOptions = useMemo(() => {
    const options = assigneeOptions.flatMap((user) =>
      user.conversationAssigneeGroups
        // Although the GraphQL query already filters users by enabled assignee groups,
        // each user may still reference disabled groups in their `conversationAssigneeGroups` field.
        // Therefore, we must manually filter out any group that is not included in the enabled list.
        .filter((group) =>
          conversationAssigneeGroupOptions.some(
            (assigneeGroup) => assigneeGroup.id === group.id,
          ),
        )
        .map((group) => ({
          groupId: group.id,
          userId: user.id,
          group: group.name,
          id: `${group.id}_${user.id}`,
          value: group.id,
          userName: `${user.firstName} ${user.lastName}`,
          label: `${group.name}/${user.label}`,
          isActive: user.isActive,
          partnerId: user.partnerId,
          avatarFileKey: user.avatarFileKey,
        })),
    );

    const groupOptions = conversationAssigneeGroupOptions.map(({ id, label }) => ({
      id,
      label,
      groupId: id,
      userId: null,
      userName: 'Unassigned',
      group: label,
      value: id,
      isActive: true,
    }));

    return [...groupOptions, ...options].sort((a, b) => b.groupId?.localeCompare(a.groupId));
  }, [assigneeOptions, conversationAssigneeGroupOptions]);

  const selectedAssignee = useMemo(() => {
    const { conversationAssigneeId, conversationAssigneeGroupId } = conversation;

    return conversationAssigneeOptions.find(({ userId, groupId }) => {
      if (conversationAssigneeId !== null) {
        return userId === conversationAssigneeId && groupId === conversationAssigneeGroupId;
      }

      return groupId === conversationAssigneeGroupId;
    });
  }, [conversationAssigneeOptions, conversation]);

  return {
    selectedAssignee: selectedAssignee ?? null,
    assigneeOptions: conversationAssigneeOptions,
  };
}
